// ============================================================================
// Display Length Constants
// ============================================================================

/**
 * Maximum character length for compact mode rendering
 * Used for minimal space, single line displays
 */
export const MAX_COMPACT_DISPLAY_LENGTH = 20;

/**
 * Maximum character length for preview mode rendering
 * Used for displaying primitive values inline in cards/lists
 */
export const MAX_PREVIEW_DISPLAY_LENGTH = 200;

/**
 * Maximum recursion depth when resolving nested structures
 * Prevents infinite loops in circular references
 */
export const MAX_RECURSION_DEPTH = 10;

// ============================================================================
// Zone Limit Constants
// ============================================================================

/**
 * Maximum number of horizontal cards to display in collapsed state (Zone 1)
 * These are compact key-value outputs shown in a 3-column grid
 */
export const ZONE_HORIZONTAL_MAX = 9;

/**
 * Maximum number of vertical cards to display in collapsed state (Zone 2)
 * These are larger outputs like paragraphs, JSON, HTML shown in a 3-column grid
 */
export const ZONE_VERTICAL_MAX = 6;

/**
 * Maximum number of large media items to display in collapsed state (Zone 3)
 * First item is full-width, remaining items in 3-column grid
 */
export const ZONE_LARGE_MEDIA_MAX = 4;

// ============================================================================
// File Preview Limits
// ============================================================================

/**
 * Largest remote file that a card will auto-download to build an inline preview.
 *
 * Above this the card renders a metadata-only placeholder (name, size, type)
 * and the bytes are fetched only when the user opens the full preview. Cards
 * are a scanning surface, so pulling tens of megabytes to draw a thumbnail
 * nobody asked for is never worth it.
 */
export const PREVIEW_MAX_FILE_BYTES = 5 * 1024 * 1024;

/**
 * Largest file we can actually open in the full preview.
 *
 * The download pulls the whole blob in one request, and the gateway cuts the
 * response body partway through, which surfaces as a bare "Network Error"
 * after a long wait. Above this we say so up front and offer the download
 * instead of starting a request that cannot finish. Raising this is pointless
 * until the file service supports ranged reads.
 */
export const OPENABLE_MAX_FILE_BYTES = 20 * 1024 * 1024;

// ============================================================================
// Value Chip Constants
// ============================================================================

/**
 * Maximum characters shown on a value chip before it is shortened.
 *
 * Wide enough that middle-truncated filenames keep both their distinguishing
 * tail and their extension — the previous 20-character head truncation
 * collapsed sibling files (`…Part1 1st to 15th.txt` / `…Part2 16th to End.txt`)
 * into identical chips.
 */
export const CHIP_MAX_LABEL_CHARS = 32;

/**
 * Complex array items rendered before the rest collapse behind a "+N more"
 * toggle. Keeps a step's height a function of how many values it produced
 * rather than how large they are.
 */
export const ARRAY_CHIP_CLAMP = 6;

/**
 * Lines of a markdown output a preview renders. Cut on whole lines, since a
 * character cut can land mid-table and break its syntax.
 */
export const MARKDOWN_PREVIEW_MAX_LINES = 40;

/**
 * A markdown output inside a preview card. The card's padding is the only
 * inset: the first and last blocks drop their outer margins, and table cells
 * are as tight as the other card tables. Markdown wraps each table in its own
 * scroller, and a card shows no scrollbars.
 */
export const MARKDOWN_PREVIEW_CLASSNAME =
  '[&_*]:overflow-hidden [&>div>:first-child]:mt-0 [&>div>:last-child]:mb-0 [&_td]:px-2 [&_td]:py-1 [&_th]:px-2 [&_th]:py-1';

/**
 * YAML serializer options shared by every view of an output, so a preview card
 * and the full-screen viewer show the same lines. `lineWidth: 0` never folds
 * long values; the viewer wraps them instead.
 */
export const OUTPUT_YAML_OPTIONS = {
  aliasDuplicateObjects: false,
  lineWidth: 0,
} as const;
